import { Component, forwardRef, OnInit } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PurchaseOrderClient, SearchPurchaseOrderRequest } from '../../api-connector/sso-connector';
import { BaseAutocompleteControl } from '../base-autocomplete-control';

export interface PurchaseOrderOption {
    id: string;
    code: string;
    supplierId?: number;
    supplierName: string;
    totalOrder: number;
    createdTime: Date;
}

@Component({
    selector: 'app-purchase-order-search-control',
    templateUrl: './purchase-order-search-control.component.html',
    styleUrls: ['./purchase-order-search-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PurchaseOrderSearchControlComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PurchaseOrderSearchControlComponent),
            multi: true,
        }
    ]
})
export class PurchaseOrderSearchControlComponent extends BaseAutocompleteControl<PurchaseOrderOption> implements OnInit {

    constructor(private purchaseOrderClient: PurchaseOrderClient) {
        super();
    }

    ngOnInit(): void {
        if (!this.placeholder || this.placeholder === '---Lựa chọn---') {
            this.placeholder = 'Tìm mã phiếu nhập hàng...';
        }
        super.ngOnInit();
    }

    protected fetchItems(searchValue: string): Observable<PurchaseOrderOption[]> {
        const request = new SearchPurchaseOrderRequest({
            keySearch: searchValue || '',
            pageIndex: 0,
            pageSize: 20,
            unpaidSuppliers: false,
            isSearchError: false
        });

        return this.purchaseOrderClient.listPurchaseOrder(request).pipe(
            map((response: any) => {
                if (!response?.items) return [];
                return response.items.map((po: any) => ({
                    id: po.id?.toString(),
                    code: po.code,
                    supplierId: po.supplierId,
                    supplierName: po.supplierName,
                    totalOrder: po.totalOrder,
                    createdTime: po.createdTime ? new Date(po.createdTime) : new Date()
                }));
            }),
            catchError(() => of([]))
        );
    }

    getDisplayValue(item: PurchaseOrderOption | null): string {
        return item?.code || '';
    }

    protected isValidItem(value: any): boolean {
        return value && value.id && value.code;
    }

    writeValue(obj: any): void {
        if (this.isValidItem(obj)) {
            super.writeValue(obj);
        } else if (obj) {
            const request = new SearchPurchaseOrderRequest({
                keySearch: obj.toString(),
                pageIndex: 0,
                pageSize: 1,
                unpaidSuppliers: false,
                isSearchError: false
            });

            this.purchaseOrderClient.listPurchaseOrder(request).pipe(
                map((response: any) => {
                    if (!response?.items || response.items.length === 0) return null;
                    const po = response.items[0];
                    return {
                        id: po.id?.toString(),
                        code: po.code,
                        supplierId: po.supplierId,
                        supplierName: po.supplierName,
                        totalOrder: po.totalOrder,
                        createdTime: po.createdTime ? new Date(po.createdTime) : new Date()
                    };
                }),
                catchError(() => of(null))
            ).subscribe((item: any) => {
                if (item) {
                    super.writeValue(item);
                }
            });
        } else {
            super.writeValue(obj);
        }
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    }

    formatDateTime(date: Date): string {
        if (!date) return '';
        return new Date(date).toLocaleString('vi-VN');
    }
}
